import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { UserPlus, Loader2, Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../../services/api';
import toast from '../../services/toast';
import { AuthSplitShell } from '../../components/auth/AuthSplitShell';
import Input from '../../components/ui/Input';
import {
  authLabelClass,
  authPrimaryButton,
  authFooterClass,
  authFooterLink,
  authPasswordToggle,
} from '../../components/auth/authViewTheme';

const Register = ({ hasUsers = true }: any) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: { username: '', email: '', password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async ({ username, email, password, confirmPassword }) => {
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    try {
      await authAPI.register(username, email, password);
      localStorage.setItem('isAuthenticated', 'true');
      navigate('/setup/organisation', { replace: true });
    } catch (err) {
      toast.error((err as Error).message || 'Registration failed. Please try again.');
    }
  });

  return (
    <AuthSplitShell title="Create account" description="Sign up for VantagePoint">
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        <div className="space-y-4">
          <div>
            <label htmlFor="username" className={authLabelClass}>
              Username
            </label>
            <Input
              id="username"
              type="text"
              autoComplete="username"
              placeholder="Choose a username"
              {...register('username', { required: true })}
            />
          </div>
          <div>
            <label htmlFor="email" className={authLabelClass}>
              Email (optional)
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              {...register('email')}
            />
          </div>
          <div>
            <label htmlFor="password" className={authLabelClass}>
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                minLength={6}
                autoComplete="new-password"
                placeholder="At least 6 characters"
                className="pr-11"
                {...register('password', { required: true, minLength: 6 })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className={authPasswordToggle}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="confirmPassword" className={authLabelClass}>
              Confirm password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm your password"
                className="pr-11"
                {...register('confirmPassword', { required: true })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((p) => !p)}
                className={authPasswordToggle}
                aria-label={
                  showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'
                }
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>
        </div>

        <button type="submit" disabled={isSubmitting} className={authPrimaryButton}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Creating account…
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
              Create account
            </>
          )}
        </button>

        {hasUsers ? (
          <p className={authFooterClass}>
            Already have an account?{' '}
            <Link to="/login" className={authFooterLink}>
              Sign in here
            </Link>
          </p>
        ) : null}
      </form>
    </AuthSplitShell>
  );
};

export default Register;
